import React, { useCallback, useMemo, useState } from 'react';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { useStaticPermission } from 'common/auth/Authorize';
import {
  DataTable,
  DataTableBulkActions,
  DataTableToolbar,
  DeleteBulkAction,
  DataTablePaginator
} from '@utmost.co/utmost-design-system';
import { useDialogWithUids, IconButton } from '@beeline.com/bp-design-system';
import { Importer } from '@oc/pages/importers_rsi/Importer';
import { useTenantConfig } from 'src/utils/hooks/useTenantConfig';
import { JobProfileDeleteDialog } from './JobProfileDeleteDialog';
import { JobProfileModal } from './JobProfileModal';
import { EngagementJobProfile } from './jobProfiles.mutations';
import { useJobProfileCells } from './jobProfileTableCells';
import { byCountryName, useJobProfileData } from './useJobProfileData';
import { useJobProfilesIntl } from './translations/useJobProfilesIntl';
import { ImportEntityTypeModel } from 'src/graphql/types';
import { JobProfileExporter } from '@oc/pages/settings/Engagements/JobCatalogues/jobProfiles/JobProfilesExporter';

export const JobProfilesTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useJobProfilesIntl();
  const tenantConfig = useTenantConfig();
  const [addModalOpen, setAddModal] = useState<boolean>(false);
  const [selectedJobProfiles, setSelectedJobProfiles] = useState<EngagementJobProfile[]>([]);
  const [selectedJobProfile, setSelectedJobProfile] = useState<EngagementJobProfile | null>(null);
  const [showJobProfileImport, setJobProfileImport] = useState(false);
  const { CREATE, DELETE, IMPORT } = useStaticPermission('JOB_PROFILE', true);
  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('ENGAGEMENTS_JOB_FAMILY');
  const {
    remove,
    getProfiles,
    setVariables,
    variables,
    totalCount,
    jobProfiles: jobProfilesData,
    loading,
    refetch
  } = useJobProfileData();
  const showJobClassifications =
    tenantConfig?.tenantConfig?.workdayConfig?.syncReferences?.find(
      (ref) => ref.referenceIdType === 'Job_Classification_Reference_ID' && ref.merge
    ) != null;

  const jobProfiles = useMemo(
    () =>
      jobProfilesData.map((jobProfile) => {
        const countries = jobProfile.countries ? [...jobProfile.countries] : [];
        return {
          ...jobProfile,
          countries: countries.sort(byCountryName)
        };
      }),
    [jobProfilesData]
  );

  const resultMessage = loading
    ? intl.loading
    : intl.viewingXJobProfiles({
        totalCount,
        pageDataCount: jobProfiles.length
      });

  const deleteDialog = useDialogWithUids([]);

  const handleShowDeleteDialog = useCallback(
    (uids: string[]) => {
      deleteDialog.show(uids);
    },
    [deleteDialog]
  );

  const columns = useJobProfileCells(handleShowDeleteDialog, setSelectedJobProfile, showJobClassifications, refetch);

  const handleDeleteAll = useCallback(
    async (uids: string[]) => {
      await remove(uids);
      getProfiles({ variables: { query: variables?.query, page: variables?.page } });
    },
    [remove, variables, getProfiles]
  );

  const handleConfirmDelete = useCallback(async () => {
    try {
      await handleDeleteAll(deleteDialog.uids);
      deleteDialog.close();
    } catch {}
  }, [handleDeleteAll, deleteDialog]);

  return (
    <>
      {(addModalOpen || !!selectedJobProfile) && (
        <JobProfileModal
          addModalOpen
          editableResource={selectedJobProfile}
          setAddModal={(e) => {
            setAddModal(e);
            setSelectedJobProfile(null);
          }}
          onComplete={() => getProfiles({ variables: { query: variables?.query, page: variables?.page } })}
        />
      )}

      <DataTable
        onVariablesChanged={setVariables}
        data={jobProfiles}
        columns={columns}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        selectable="uid"
        tableHeight="content"
        isFetching={loading}
        totalCount={totalCount}
        dataTest="job-profiles-list"
      >
        <DataTableBulkActions<EngagementJobProfile> onSelect={setSelectedJobProfiles}>
          <DeleteBulkAction<EngagementJobProfile>
            disabled={!DELETE}
            tooltipText={intl.delete}
            onClick={(selectedRows) => deleteDialog.show(rowsToUids(selectedRows))}
          />
        </DataTableBulkActions>
        <DataTableToolbar
          afterActions={
            <>
              <IconButton
                iconName="download"
                dataTest="import_job-profiles"
                disabled={!IMPORT}
                onClick={() => setJobProfileImport(true)}
                tooltipText={intl.import}
              />
              {IMPORT && showJobProfileImport && (
                <Importer
                  allowedImportTypes={[{ id: 'JOB_PROFILE', entityType: ImportEntityTypeModel.JobProfile }]}
                  key="JOB_PROFILE"
                  setModal={setJobProfileImport}
                />
              )}
            </>
          }
          addButtonProps={{
            onAdd: () => setAddModal(true),
            disabled: !CREATE,
            addDataTest: 'job-profiles__add-job-profile-button'
          }}
          searchProps={{
            value: variables?.query?.search || ''
          }}
          message={resultMessage}
        />

        <DataTablePaginator />
        <JobProfileExporter />
      </DataTable>
      <JobProfileDeleteDialog
        show={deleteDialog.visible}
        isLoading={loading}
        jobProfiles={selectedJobProfiles}
        uids={deleteDialog.uids}
        onSubmit={handleConfirmDelete}
        onClose={() => deleteDialog.close()}
      />
    </>
  );
};

function rowsToUids(selectedRows: { uid?: string }[]): string[] {
  return selectedRows.map((row) => row?.uid ?? '');
}
